import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Play,
  Image as ImageIcon,
  Video,
  Mic,
  Wand2,
  Download,
  Loader2,
  CheckCircle,
  Upload,
  Settings,
  Share2,
  Archive,
  Trash2,
  Send,
  ShoppingCart,
  Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imageStyles = [
  { value: "cinematic", label: "Cinematic", icon: "🎬" },
  { value: "realistic", label: "Realistic", icon: "📷" },
  { value: "animated", label: "Animated", icon: "🎨" },
  { value: "minimal", label: "Minimal", icon: "✨" }
];

const videoDurations = [
  { value: 4, label: "4 sec", icon: "⚡", provider: "sora" },
  { value: 8, label: "8 sec", icon: "🎬", provider: "sora" },
  { value: 12, label: "12 sec", icon: "📽️", provider: "sora" },
  { value: 30, label: "30 sec", icon: "🎥", provider: "extended" },
  { value: 60, label: "1 min", icon: "🎞️", provider: "extended" },
  { value: 120, label: "2 min", icon: "🎦", provider: "extended" }
];

const voiceStyles = [
  { id: "alloy", name: "Alloy", desc: "Neutral", icon: "🎙️" },
  { id: "nova", name: "Nova", desc: "Energetic", icon: "⚡" },
  { id: "onyx", name: "Onyx", desc: "Deep", icon: "🔊" },
  { id: "shimmer", name: "Shimmer", desc: "Bright", icon: "✨" },
  { id: "echo", name: "Echo", desc: "Calm", icon: "🌊" },
  { id: "fable", name: "Fable", desc: "Expressive", icon: "📖" }
];

export default function VideoStudio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualMode, setManualMode] = useState(false);
  const [activeTab, setActiveTab] = useState("auto");

  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatingVoice, setGeneratingVoice] = useState(false);

  const [imagePrompt, setImagePrompt] = useState("");
  const [imageStyle, setImageStyle] = useState("cinematic");
  const [videoDuration, setVideoDuration] = useState(8);
  const [voiceText, setVoiceText] = useState("");
  const [selectedVoice, setSelectedVoice] = useState("alloy");
  const [uploadedImages, setUploadedImages] = useState([]);

  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [autoProgress, setAutoProgress] = useState(0);
  const [videoComplete, setVideoComplete] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Video action handlers
  const handleArchive = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/archive`);
      toast.success("Video archived");
      navigate("/library");
    } catch (error) {
      toast.error("Failed to archive");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this video?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success("Video deleted");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleBroadcast = () => {
    navigate(`/broadcast/${projectId}`);
  };

  const handleAddBuyNow = async () => {
    const price = prompt("Enter price for Buy Now button:", "99.99");
    if (!price) return;
    
    try {
      await axios.post(`${API}/projects/${projectId}/add-buy-now`, {
        price: parseFloat(price),
        payment_methods: ["stripe", "paypal"],
        shipping_enabled: true
      });
      toast.success("Buy Now button added!");
    } catch (error) {
      toast.error("Failed to add Buy Now");
    }
  };

  useEffect(() => {
    const loadProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${projectId}`);
        setProject(response.data);
        if (response.data.script) {
          setVoiceText(response.data.script.slice(0, 1000));
        }
        if (response.data.description) {
          setImagePrompt(response.data.description);
        }
      } catch (error) {
        toast.error("Project not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [projectId, navigate]);

  // Auto-generate everything
  const generateAll = async () => {
    if (!project?.script) {
      toast.error("No script found");
      return;
    }

    setGenerating(true);
    setAutoProgress(0);

    try {
      // Step 1: Generate images
      setAutoProgress(20);
      toast.info("Creating visuals...");
      const imageResponse = await axios.post(`${API}/images/generate`, {
        prompt: project.description || project.script.slice(0, 200),
        provider: "openai",
        style: imageStyle
      });
      setGeneratedImage(imageResponse.data);

      // Step 2: Generate voice
      setAutoProgress(50);
      toast.info("Creating voice-over...");
      const voiceResponse = await axios.post(`${API}/voices/generate`, {
        text: project.script.slice(0, 1000),
        provider: "openai",
        voice: selectedVoice,
        model: "tts-1-hd"
      });
      setGeneratedAudio(voiceResponse.data);

      // Step 3: Generate video
      setAutoProgress(80);
      toast.info("Creating video clip...");
      const videoResponse = await axios.post(`${API}/videos/generate`, {
        project_id: projectId,
        prompt: project.description || project.script.slice(0, 200),
        provider: "sora",
        duration: videoDuration, // Uses 4, 8, or 12
        size: "1280x720"
      });
      
      // Check if video was actually generated with a URL
      if (videoResponse.data && videoResponse.data.url) {
        setGeneratedVideo(videoResponse.data);
        setAutoProgress(100);
        setVideoComplete(true);
        setShowActions(true);
        toast.success("🎬 Video created successfully! Click to play.");
      } else if (videoResponse.data && videoResponse.data.status === "pending_assembly") {
        // Video is being processed
        setAutoProgress(100);
        toast.info("Video is processing. Voice and images are ready!");
        // Still show what we have
        setGeneratedVideo(null);
      } else {
        setAutoProgress(100);
        toast.info("Voice and images ready. Video processing may take a moment.");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error("Generation paused - you can continue manually");
      setManualMode(true);
      setActiveTab("image");
    } finally {
      setGenerating(false);
    }
  };

  const generateImage = async () => {
    if (!imagePrompt) {
      toast.error("Enter an image description");
      return;
    }
    
    setGeneratingImage(true);
    try {
      const response = await axios.post(`${API}/images/generate`, {
        prompt: imagePrompt,
        provider: "openai",
        style: imageStyle
      });
      setGeneratedImage(response.data);
      toast.success("Image created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate image");
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateVoice = async () => {
    if (!voiceText) {
      toast.error("Enter text for voice-over");
      return;
    }
    
    setGeneratingVoice(true);
    try {
      const response = await axios.post(`${API}/voices/generate`, {
        text: voiceText,
        provider: "openai",
        voice: selectedVoice,
        model: "tts-1-hd"
      });
      setGeneratedAudio(response.data);
      toast.success("Voice-over created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate voice");
    } finally {
      setGeneratingVoice(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));
    setUploadedImages(prev => [...prev, ...newImages]);
    toast.success(`${files.length} image(s) added`);
  };

  const removeUploadedImage = (id) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loading-spinner w-10 h-10" />
      </div>
    );
  }

  const tabs = manualMode ? [
    { id: "image", label: "Images", icon: "🖼️" },
    { id: "voice", label: "Voice", icon: "🎙️" },
    { id: "upload", label: "Upload", icon: "📤" }
  ] : [
    { id: "auto", label: "Auto", icon: "✨" }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-text-secondary hover:text-text-primary"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="h-5 w-px bg-border" />
            <h1 className="font-heading font-bold text-sm text-text-primary truncate max-w-[200px]">
              {project?.title}
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-text-muted">Manual</span>
              <Switch 
                checked={manualMode} 
                onCheckedChange={(v) => {
                  setManualMode(v);
                  setActiveTab(v ? "image" : "auto");
                }}
                data-testid="manual-toggle"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-border text-xs"
              onClick={() => navigate(`/broadcast/${projectId}`)}
              data-testid="broadcast-btn"
            >
              <Share2 className="w-3 h-3 mr-1" />
              Broadcast
            </Button>
            <Button
              size="sm"
              className="bg-primary hover:bg-primary-hover text-white text-xs"
              disabled={!generatedVideo && !generatedAudio}
              data-testid="export-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Export
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Preview */}
        <div className="flex-1 p-4 flex flex-col min-w-0">
          <div className="flex-1 rounded-xl bg-background-paper border border-border overflow-hidden relative min-h-[300px]">
            {generatedVideo && generatedVideo.url ? (
              <video
                src={generatedVideo.url.startsWith('http') ? generatedVideo.url : `${process.env.REACT_APP_BACKEND_URL}${generatedVideo.url}`}
                controls
                autoPlay
                className="w-full h-full object-contain"
                data-testid="video-preview"
              />
            ) : generatedImage ? (
              <img
                src={generatedImage.url.startsWith('http') ? generatedImage.url : `${process.env.REACT_APP_BACKEND_URL}${generatedImage.url}`}
                alt="Generated"
                className="w-full h-full object-contain"
                data-testid="image-preview"
              />
            ) : uploadedImages.length > 0 ? (
              <img
                src={uploadedImages[0].url}
                alt="Uploaded"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center flex-col gap-3">
                <Video className="w-12 h-12 text-text-muted" />
                <p className="text-text-muted text-sm">
                  {manualMode ? "Generate or upload content" : "Click 'Create Video' to start"}
                </p>
              </div>
            )}
          </div>

          {/* Progress bar for auto mode */}
          {generating && (
            <div className="mt-4 bg-background-paper rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-primary">Creating your video...</span>
                <span className="text-sm text-primary">{autoProgress}%</span>
              </div>
              <div className="h-2 bg-background rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${autoProgress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          )}

          {/* Audio player */}
          {generatedAudio && (
            <div className="mt-4 bg-background-paper rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 text-accent-success text-sm mb-2">
                <CheckCircle className="w-4 h-4" />
                Voice-over ready
              </div>
              <audio
                src={generatedAudio.audio_data}
                controls
                className="w-full h-10"
                data-testid="audio-player"
              />
            </div>
          )}

          {/* Video Actions - Show when video is complete */}
          {(showActions || generatedVideo) && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 rounded-xl bg-gradient-to-br from-accent-success/10 to-primary/10 border border-accent-success/30"
            >
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-accent-success" />
                <span className="font-medium text-text-primary">Video Ready!</span>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => {
                    const video = document.querySelector('[data-testid="video-preview"]');
                    if (video) video.play();
                  }}
                  className="bg-primary hover:bg-primary-hover text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Play
                </Button>
                
                <Button
                  onClick={handleBroadcast}
                  className="bg-accent-success hover:bg-accent-success/90 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Broadcast
                </Button>
                
                <Button
                  onClick={handleAddBuyNow}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Buy Now
                </Button>
                
                <Button
                  onClick={handleArchive}
                  variant="outline"
                  className="border-border text-text-primary"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive
                </Button>
              </div>
              
              <Button
                onClick={handleDelete}
                variant="ghost"
                className="w-full mt-2 text-accent-error hover:bg-accent-error/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Video
              </Button>
            </motion.div>
          )}
        </div>

        {/* Right Panel - Controls */}
        <div className="w-96 bg-background-paper border-l border-border flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="p-3 border-b border-border">
            <div className={`grid gap-2 ${tabs.length === 1 ? 'grid-cols-1' : 'grid-cols-3'}`}>
              {tabs.map((tab) => (
                <Card
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`cursor-pointer transition-all ${
                    activeTab === tab.id
                      ? 'bg-primary/10 border-primary'
                      : 'bg-background border-border hover:border-primary/50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <CardContent className="p-3 text-center">
                    <span className="text-xl">{tab.icon}</span>
                    <div className="text-xs text-text-primary mt-1">{tab.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Auto Tab */}
            {activeTab === "auto" && !manualMode && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="text-4xl mb-3">✨</div>
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">
                    Automatic Creation
                  </h3>
                  <p className="text-sm text-text-muted mb-6">
                    We'll create images, voice-over, and video from your script automatically.
                  </p>
                </div>

                <div>
                  <Label className="text-text-primary text-sm font-medium mb-2 block">Duration</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {videoDurations.map((d) => (
                      <Card
                        key={d.value}
                        onClick={() => setVideoDuration(d.value)}
                        className={`cursor-pointer transition-all ${
                          videoDuration === d.value
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                        data-testid={`duration-${d.value}`}
                      >
                        <CardContent className="p-2 text-center">
                          <span className="text-base">{d.icon}</span>
                          <div className="text-xs text-text-primary mt-1">{d.label}</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-text-primary text-sm font-medium mb-2 block">Voice</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {voiceStyles.slice(0, 4).map((voice) => (
                      <Card
                        key={voice.id}
                        onClick={() => setSelectedVoice(voice.id)}
                        className={`cursor-pointer transition-all ${
                          selectedVoice === voice.id
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                        data-testid={`voice-${voice.id}`}
                      >
                        <CardContent className="p-2 flex items-center gap-2">
                          <span className="text-base">{voice.icon}</span>
                          <div className="text-xs text-text-primary">{voice.name}</div>
                          {selectedVoice === voice.id && (
                            <CheckCircle className="w-3 h-3 text-primary ml-auto" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-text-primary text-sm font-medium mb-2 block">Style</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {imageStyles.map((style) => (
                      <Card
                        key={style.value}
                        onClick={() => setImageStyle(style.value)}
                        className={`cursor-pointer transition-all ${
                          imageStyle === style.value
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                        data-testid={`style-${style.value}`}
                      >
                        <CardContent className="p-2 flex items-center gap-2">
                          <span className="text-base">{style.icon}</span>
                          <span className="text-xs text-text-primary">{style.label}</span>
                          {imageStyle === style.value && (
                            <CheckCircle className="w-3 h-3 text-primary ml-auto" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={generateAll}
                  disabled={generating}
                  className="w-full bg-accent-success hover:bg-accent-success/90 text-white py-6 text-base"
                  data-testid="create-video-btn"
                >
                  {generating ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creating...</>
                  ) : (
                    <><Wand2 className="w-5 h-5 mr-2" />Create Video</>
                  )}
                </Button>
              </div>
            )}

            {/* Image Tab (Manual Mode) */}
            {activeTab === "image" && manualMode && (
              <div className="space-y-4">
                <div>
                  <Label className="text-text-primary text-sm font-medium mb-2 block">Describe image</Label>
                  <Textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="A professional scene..."
                    className="bg-background border-border text-text-primary text-sm"
                    rows={3}
                    data-testid="image-prompt-input"
                  />
                </div>
                
                <div>
                  <Label className="text-text-primary text-sm font-medium mb-2 block">Style</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {imageStyles.map((style) => (
                      <Card
                        key={style.value}
                        onClick={() => setImageStyle(style.value)}
                        className={`cursor-pointer transition-all ${
                          imageStyle === style.value
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                      >
                        <CardContent className="p-2 flex items-center gap-2">
                          <span className="text-lg">{style.icon}</span>
                          <span className="text-sm text-text-primary">{style.label}</span>
                          {imageStyle === style.value && (
                            <CheckCircle className="w-4 h-4 text-primary ml-auto" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={generateImage}
                  disabled={generatingImage}
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                  data-testid="generate-image-btn"
                >
                  {generatingImage ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" />Generate Image</>
                  )}
                </Button>

                {generatedImage && (
                  <div className="p-2 rounded-lg bg-background border border-accent-success/30">
                    <div className="flex items-center gap-2 text-accent-success text-sm mb-2">
                      <CheckCircle className="w-4 h-4" />
                      Image ready!
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Voice Tab (Manual Mode) */}
            {activeTab === "voice" && manualMode && (
              <div className="space-y-4">
                <div>
                  <Label className="text-text-primary text-sm font-medium mb-2 block">Text to speak</Label>
                  <Textarea
                    value={voiceText}
                    onChange={(e) => setVoiceText(e.target.value)}
                    placeholder="Enter narration..."
                    className="bg-background border-border text-text-primary text-sm"
                    rows={4}
                    data-testid="voice-text-input"
                  />
                </div>
                
                <div>
                  <Label className="text-text-primary text-sm font-medium mb-2 block">Voice</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {voiceStyles.map((voice) => (
                      <Card
                        key={voice.id}
                        onClick={() => setSelectedVoice(voice.id)}
                        className={`cursor-pointer transition-all ${
                          selectedVoice === voice.id
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                      >
                        <CardContent className="p-2 flex items-center gap-2">
                          <span className="text-base">{voice.icon}</span>
                          <div className="flex-1">
                            <div className="text-sm text-text-primary">{voice.name}</div>
                            <div className="text-xs text-text-muted">{voice.desc}</div>
                          </div>
                          {selectedVoice === voice.id && (
                            <CheckCircle className="w-4 h-4 text-primary" />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={generateVoice}
                  disabled={generatingVoice}
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                  data-testid="generate-voice-btn"
                >
                  {generatingVoice ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                  ) : (
                    <><Mic className="w-4 h-4 mr-2" />Generate Voice</>
                  )}
                </Button>
              </div>
            )}

            {/* Upload Tab (Manual Mode) */}
            {activeTab === "upload" && manualMode && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="text-4xl mb-3">📤</div>
                  <h3 className="font-heading font-bold text-text-primary mb-2">
                    Upload Images
                  </h3>
                  <p className="text-sm text-text-muted mb-4">
                    Add your own images to create a video
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />

                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="w-full border-dashed border-2 py-8"
                  data-testid="upload-btn"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Select Images
                </Button>

                {uploadedImages.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-text-primary text-sm font-medium">Uploaded ({uploadedImages.length})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {uploadedImages.map((img) => (
                        <div key={img.id} className="relative group">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="w-full aspect-square object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removeUploadedImage(img.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
